import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";

export default function FacilitiesListPage() {
  const [q, setQ] = useState("");
  const [systemType, setSystemType] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (systemType && systemType !== "__any__") params.set("system_type", systemType);
    apiClient.get(`/facilities?${params.toString()}`)
      .then(r => { if (active) setFacilities(r.data); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [q, systemType]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="facilities-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Correctional institutions</h1>
      <p className="mt-1 text-sm text-slate-600">Browse federal, state, and county facilities and check current operating status.</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <Input data-testid="facility-search-input" placeholder="Search by facility or city..." value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={systemType} onValueChange={setSystemType}>
          <SelectTrigger data-testid="facility-system-select"><SelectValue placeholder="All systems" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">All systems</SelectItem>
            <SelectItem value="federal">Federal (BOP)</SelectItem>
            <SelectItem value="state">State prisons</SelectItem>
            <SelectItem value="county">County / Local jails</SelectItem>
          </SelectContent>
        </Select>
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 flex items-center">
          <span className="font-mono text-xs uppercase tracking-widest" data-testid="facilities-count">
            {loading ? "Loading facilities…" : `Showing ${facilities.length} facilities`}
          </span>
        </div>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        {loading && facilities.length === 0 && [1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} className="h-[86px] rounded-xl" data-testid="facility-skeleton" />
        ))}
        {facilities.map((f) => (
          <Link key={f.id} to={`/facility/${f.id}`} data-testid={`facility-tile-${f.id}`}>
            <Card className="p-4 border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{f.system}</div>
                  <div className="mt-0.5 font-heading text-lg font-bold text-slate-900 truncate">{f.name}</div>
                  <div className="mt-1 text-xs text-slate-600 truncate">
                    {[f.city, f.state].filter(Boolean).join(", ")} · {f.facility_type}
                  </div>
                </div>
                <StatusBadge status={f.status} size="sm" hideUnknown />
              </div>
            </Card>
          </Link>
        ))}
        {!loading && facilities.length === 0 && (
          <Card className="p-6 md:col-span-2 border-slate-200 text-center text-slate-600" data-testid="facilities-empty">No facilities match your filters.</Card>
        )}
      </div>
    </div>
  );
}
